import { useState, type ReactNode } from "react"
import { useSelector } from "react-redux"
import PreviewHtml from "./html/PreviewHtml"
import RelationField from "./RelationField"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { FileText, Copy, Check } from "lucide-react"
import {
  effectiveImageFieldType,
  resolveConnectorAssetPublicUrl,
} from "./utils/pictureUtils"
import Image from "./Image"

function DetailFieldBlock({
  label,
  description,
  children,
}: {
  label: string
  description?: string
  children: ReactNode
}) {
  return (
    <div className="grid grid-cols-1 gap-2 py-4 text-left border-b border-gray-200/90 last:border-b-0 sm:grid-cols-[minmax(9rem,32%)_1fr] sm:gap-x-6">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-gray-900">{label}</div>
        {description ? (
          <div className="text-xs text-gray-500 mt-0.5 leading-snug">{description}</div>
        ) : null}
      </div>
      <div className="min-w-0 text-sm text-gray-800 break-words">{children}</div>
    </div>
  )
}

function CopyableValue({ label, value, copyKey }: { label: string; value: string; copyKey: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* ignore */
    }
  }
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 shrink-0 sm:w-28">{label}</span>
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <code className="block flex-1 rounded-md bg-gray-100 px-2.5 py-1.5 text-xs text-gray-900 break-all font-mono leading-relaxed dark:bg-gray-800 dark:text-gray-100">
          {value}
        </code>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label={`Copy ${copyKey}`}
          onClick={copy}
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-gray-500" />}
        </Button>
      </div>
    </div>
  )
}

function fieldStorageKey(field: { name?: string; key?: string }): string {
  return field.name || field.key || ""
}

function formatDetailFallbackValue(value: unknown): ReactNode {
  if (value === null || value === undefined) {
    return "—"
  }
  if (typeof value === "string") {
    return value
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  try {
    return (
      <pre className="whitespace-pre-wrap break-words rounded-md bg-gray-50 px-2 py-1.5 text-xs font-mono text-gray-900 dark:bg-gray-900/40 dark:text-gray-100">
        {JSON.stringify(value, null, 2)}
      </pre>
    )
  } catch {
    return String(value)
  }
}

function formatDateTimeDisplay(raw: unknown): string {
  if (raw == null || raw === "") {
    return ""
  }
  if (typeof raw === "number") {
    const ms = raw < 1e12 ? raw * 1000 : raw
    return new Date(ms).toLocaleString()
  }
  const n = Number(raw)
  if (!Number.isNaN(n) && String(raw).trim() !== "") {
    const ms = n < 1e12 ? n * 1000 : n
    return new Date(ms).toLocaleString()
  }
  const d = new Date(String(raw))
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString()
}

function formatCalendarDates(raw: unknown): string {
  if (!Array.isArray(raw) || raw.length === 0) {
    return ""
  }
  const parts: string[] = []
  for (const d of raw) {
    if (d == null) {
      continue
    }
    if (d instanceof Date) {
      parts.push(d.toLocaleDateString())
      continue
    }
    if (typeof d === "number") {
      const ms = d < 1e12 ? d * 1000 : d
      parts.push(new Date(ms).toLocaleDateString())
      continue
    }
    const t = Date.parse(String(d))
    if (!Number.isNaN(t)) {
      parts.push(new Date(t).toLocaleDateString())
    }
  }
  return parts.join(", ")
}

const ItemDetails = ({
  itemData: itemData,
  meta: meta,
}: {
  itemData: any
  meta: any
  onEdit: any
  index: number
}) => {
  const user = useSelector((state: any) => state.user)

  const getFieldValueFromNewComponent = (fieldKey: string) => {
    if (itemData) {
      if (itemData?.find) {
        return itemData?.find((element: any) => element.key === fieldKey)?.value
      } else {
        return itemData[fieldKey]
      }
    } else {
      return null
    }
  }

  const getNestedValue = (obj: any, path?: string) => {
    if (!obj || typeof obj !== "object" || !path) return undefined
    if (!path.includes(".")) return obj[path]
    return path.split(".").reduce((acc: any, key: string) => (acc && typeof acc === "object" ? acc[key] : undefined), obj)
  }

  const getSystemRelationValue = (item: any, field: any) => {
    if (!item || !field) return undefined
    if (field.sourceField) {
      return getNestedValue(item, field.sourceField)
    }
    if (field.value === "forms" && item.registrationFormUid) {
      return item.registrationFormUid
    }
    return getNestedValue(item, field.name)
  }

  const rawFormUid = itemData && typeof itemData === "object" && !Array.isArray(itemData) ? itemData._formUid : null
  const formUid =
    typeof rawFormUid === "string"
      ? rawFormUid.trim()
      : rawFormUid != null
        ? String(rawFormUid).trim()
        : ""
  const showFormSubmissionBlock = Boolean(formUid)

  const submitterEmail =
    itemData && typeof itemData._submitterEmail === "string" ? itemData._submitterEmail.trim() : ""
  const itemUid = itemData && typeof itemData.uid === "string" ? itemData.uid.trim() : ""

  return (
    <div className="w-full max-w-2xl mx-auto text-left">
      {showFormSubmissionBlock && (
        <Card className="mb-6 border-violet-200/80 bg-violet-50/40 shadow-sm dark:border-violet-900/50 dark:bg-violet-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <FileText className="h-5 w-5 text-violet-600 dark:text-violet-400 shrink-0" aria-hidden />
              Form submission
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <CopyableValue label="Form UID" value={formUid} copyKey="form uid" />
            {submitterEmail ? (
              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500 shrink-0 sm:w-28">
                  Submitter
                </span>
                <span className="text-sm text-gray-900 break-all dark:text-gray-100">{submitterEmail}</span>
              </div>
            ) : null}
            {itemUid ? (
              <CopyableValue label="Record UID" value={itemUid} copyKey="record uid" />
            ) : null}
          </CardContent>
        </Card>
      )}

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-950/40">
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Fields</h3>
          <p className="text-xs text-gray-500 mt-0.5">Read-only view</p>
        </div>
        <div className="px-4 pb-2">
          {(meta?.fields || []).map((field: any, index: number) => {
            const rawValue = getFieldValueFromNewComponent(fieldStorageKey(field))
            const fieldType = effectiveImageFieldType(field, rawValue)
            switch (fieldType) {
              case "instruction": {
                const html = typeof field?.value === "string" ? field.value : ""
                if (!html.trim()) {
                  return null
                }
                return (
                  <div
                    key={index}
                    className="py-4 text-left border-b border-gray-200/90 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200 [&_p]:mb-2 [&_p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                )
              }
              case "text":
              case "textarea":
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {rawValue}
                  </DetailFieldBlock>
                )
              case "datetime":
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {formatDateTimeDisplay(getFieldValueFromNewComponent(fieldStorageKey(field)))}
                  </DetailFieldBlock>
                )
              case "date":
              case "dateonly": {
                const dateOnlyValue = getFieldValueFromNewComponent(fieldStorageKey(field))
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {dateOnlyValue ? new Date(dateOnlyValue).toLocaleDateString() : ""}
                  </DetailFieldBlock>
                )
              }
              case "timeonly": {
                const timeOnlyValue = getFieldValueFromNewComponent(fieldStorageKey(field))
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {timeOnlyValue ? new Date(timeOnlyValue).toLocaleTimeString("en-GB") : ""}
                  </DetailFieldBlock>
                )
              }
              case "calendar": {
                const calRaw = getFieldValueFromNewComponent(fieldStorageKey(field))
                const line = formatCalendarDates(calRaw)
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {line || "—"}
                  </DetailFieldBlock>
                )
              }
              case "number": {
                const numberValue = getFieldValueFromNewComponent(fieldStorageKey(field))
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {numberValue !== null && numberValue !== undefined
                      ? typeof numberValue === "number"
                        ? numberValue.toLocaleString()
                        : numberValue
                      : ""}
                  </DetailFieldBlock>
                )
              }
              case "select":
              case "dropdown":
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {getFieldValueFromNewComponent(fieldStorageKey(field))}
                  </DetailFieldBlock>
                )
              case "checkbox": {
                const cb = getFieldValueFromNewComponent(fieldStorageKey(field))
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {cb === true ? "Yes" : cb === false ? "No" : "—"}
                  </DetailFieldBlock>
                )
              }
              case "code": {
                const codeVal = getFieldValueFromNewComponent(fieldStorageKey(field))
                const s = codeVal != null && String(codeVal).trim() !== "" ? String(codeVal) : ""
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    <code className="block rounded-md bg-gray-100 px-2.5 py-1.5 text-xs font-mono text-gray-900 break-all dark:bg-gray-800 dark:text-gray-100">
                      {s || "—"}
                    </code>
                  </DetailFieldBlock>
                )
              }
              case "image": {
                const rawValue = getFieldValueFromNewComponent(fieldStorageKey(field))
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    <Image
                      filename={rawValue}
                      alt={field.label || "Picture"}
                      readOnly
                      connectorAssetPublicUrl={resolveConnectorAssetPublicUrl({
                        ...meta,
                        slug: meta?.slug,
                      })}
                      thumbClassName="max-h-48 w-auto max-w-full rounded-lg border border-gray-200 object-contain dark:border-gray-600"
                    />
                  </DetailFieldBlock>
                )
              }
              case "file": {
                const publicUrl = import.meta.env.VITE_PUBLIC_URL || ""
                const fn = getFieldValueFromNewComponent(fieldStorageKey(field))
                const filename = fn != null && String(fn).trim() !== "" ? String(fn).trim() : ""
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    {filename ? (
                      <a
                        href={`${publicUrl}/uploads/${filename}`}
                        className="text-blue-600 underline break-all dark:text-blue-400"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {filename}
                      </a>
                    ) : (
                      "—"
                    )}
                  </DetailFieldBlock>
                )
              }
              case "richtext":
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    <PreviewHtml label={field.label} value={getFieldValueFromNewComponent(field.name)} />
                  </DetailFieldBlock>
                )
              case "multiplerelation":
              case "multiplerelations":
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    <RelationField
                      readOnlyUids={getFieldValueFromNewComponent(field.name)}
                      table={field.value}
                      fields={field.fields}
                      multiple={true}
                      onChange={() => { }}
                      allowedGroups={user.info?.groups}
                      label={field.label}
                      relationFieldKey={field.name}
                      canEdit={false}
                      listOrganization={meta?.organization}
                    />
                  </DetailFieldBlock>
                )
              case "relation":
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    <RelationField
                      readOnlyUids={getFieldValueFromNewComponent(field.name)}
                      table={field.value}
                      fields={field.fields}
                      multiple={true}
                      onChange={() => { }}
                      allowedGroups={user.info?.groups}
                      label={field.label}
                      relationFieldKey={field.name}
                      canEdit={false}
                      listOrganization={meta?.organization}
                    />
                  </DetailFieldBlock>
                )
              case "systemrelation": {
                const sourceValue =
                  getSystemRelationValue(itemData, field) ?? getFieldValueFromNewComponent(field.sourceField || field.name)
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    <RelationField
                      readOnlyUids={sourceValue}
                      table={field.value}
                      systemSource={field.value}
                      fields={field.fields}
                      multiple={false}
                      onChange={() => { }}
                      allowedGroups={user.info?.groups}
                      label={field.label}
                      relationFieldKey={field.name}
                      canEdit={false}
                      listOrganization={meta?.organization}
                    />
                  </DetailFieldBlock>
                )
              }
              default: {
                const raw = getFieldValueFromNewComponent(fieldStorageKey(field))
                const typeLabel =
                  field?.type != null && String(field.type).trim() !== ""
                    ? String(field.type)
                    : "unknown"
                return (
                  <DetailFieldBlock key={index} label={field.label} description={field.description}>
                    <div className="space-y-1.5">
                      {formatDetailFallbackValue(raw)}
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        No dedicated viewer for field type &quot;{typeLabel}&quot;.
                      </p>
                    </div>
                  </DetailFieldBlock>
                )
              }
            }
          })}
        </div>
      </div>
    </div>
  )
}
export default ItemDetails
